import { useId, useState } from "react";
import { ApiError } from "@spf/api-client";
import { api } from "../lib/client.js";
import { TextField } from "../components/TextField.js";
import { Alert } from "../components/Alert.js";
import { buttonClass } from "../components/ViewState.js";

interface Props {
  onSignedIn: () => void;
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError && caught.status === 401) {
    return "Email or password is incorrect.";
  }
  if (caught instanceof ApiError && caught.status === 429) {
    return "Too many attempts. Wait a minute and try again.";
  }
  return "Could not sign in. Check your connection and try again.";
}

export function LoginPage({ onSignedIn }: Props) {
  const errorId = useId();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // The client stores the pair itself, so nothing here can drop the
      // refresh token and leave a session that expires in fifteen minutes.
      await api.login(email, password);
      onSignedIn();
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setSubmitting(false);
    }
  }

  const describedBy = error !== null ? errorId : undefined;

  return (
    <main className="grid min-h-dvh place-items-center bg-page p-6">
      <form
        className="flex w-full max-w-sm flex-col gap-4 rounded-card border border-line bg-card p-6 sm:p-7"
        onSubmit={(e) => void submit(e)}
        noValidate
      >
        <h1 className="text-xl font-semibold tracking-tight text-ink">Sign in</h1>

        {error !== null && <Alert id={errorId}>{error}</Alert>}

        <TextField
          label="Email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={setEmail}
          describedBy={describedBy}
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={setPassword}
          describedBy={describedBy}
        />

        <button className={buttonClass} type="submit" disabled={submitting}>
          {submitting ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </main>
  );
}
